import React from 'react';
import { MENU_ICON_PATHS } from './menuIcons';

export interface SideMenuSubItem {
  key: string;
  label: string;
  onClick: () => void;
  isDanger?: boolean;
}

export interface SideMenuItem {
  key: string;
  label: string;
  onClick?: () => void;
  isActive?: boolean;
  isDanger?: boolean;
  icon?: React.ReactNode;
  subItems?: SideMenuSubItem[];
}

interface SideMenuCardProps {
  title?: string;
  items: SideMenuItem[];
}

const SideMenuCard: React.FC<SideMenuCardProps> = ({ items }) => {
  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem('trafficLiteMenuCollapsed') === '1';
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem('trafficLiteMenuCollapsed', collapsed ? '1' : '0');
    } catch {
      // no-op
    }
  }, [collapsed]);

  const [openSubmenuKey, setOpenSubmenuKey] = React.useState<string | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target as Node)) {
        setOpenSubmenuKey(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (collapsed) {
      setOpenSubmenuKey(null);
    }
  }, [collapsed]);

  const getFallbackIcon = (label: string) => {
    const trimmed = (label || '').trim();
    if (!trimmed) return '•';
    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
    return trimmed.slice(0, 2).toUpperCase();
  };

  const renderIcon = (icon: React.ReactNode, label: string) => {
    if (typeof icon === 'string') {
      return (
        <img
          src={icon}
          alt={label}
          style={{
            width: '17px',
            height: '17px',
            objectFit: 'contain'
          }}
        />
      );
    }
    return icon;
  };

  return (
    <aside className={`${collapsed ? 'w-[74px]' : 'w-[210px]'} shrink-0 transition-all duration-200`} ref={wrapperRef}>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-2">
        <div className="px-1 pb-1 flex justify-end">
          <button
            type="button"
            onClick={() => setCollapsed((prev) => !prev)}
            aria-label={collapsed ? 'Expand menu' : 'Minimize menu'}
            title={collapsed ? 'Expand' : 'Minimize'}
            className="traffic-menu-font rounded-md transition-colors"
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              color: '#4b5563',
              fontSize: '20px',
              fontWeight: 700,
              minWidth: '32px',
              minHeight: '32px',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >
            <img
              src={collapsed ? MENU_ICON_PATHS.rightArrows : MENU_ICON_PATHS.leftArrows}
              alt=""
              style={{ width: '16px', height: '16px', objectFit: 'contain' }}
            />
          </button>
        </div>
        <nav className="flex flex-col">
          {items.map((item) => {
            const hasSubItems = Boolean(item.subItems && item.subItems.length > 0);
            const isSubmenuOpen = openSubmenuKey === item.key;
            return (
              <div key={item.key} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => {
                    if (hasSubItems) {
                      setOpenSubmenuKey((prev) => (prev === item.key ? null : item.key));
                      return;
                    }
                    item.onClick?.();
                    setOpenSubmenuKey(null);
                  }}
                  className="traffic-menu-font text-left px-3 py-2 rounded-md text-[15px] transition-colors w-full"
                  style={{
                    color: item.isDanger ? '#b91c1c' : '#212529',
                    backgroundColor: item.isActive ? '#f3f4f6' : 'transparent',
                    fontWeight: item.isActive ? 600 : 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'space-between',
                    gap: '10px'
                  }}
                  title={collapsed ? item.label : undefined}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      gap: '10px',
                      minWidth: 0
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        minWidth: '20px',
                        fontSize: '13px',
                        lineHeight: 1
                      }}
                    >
                      {item.icon ? renderIcon(item.icon, item.label) : getFallbackIcon(item.label)}
                    </span>
                    {!collapsed && <span>{item.label}</span>}
                  </span>
                  {!collapsed && hasSubItems && (
                    <span aria-hidden="true" style={{ color: '#6b7280', display: 'inline-flex', alignItems: 'center' }}>
                      <img
                        src={isSubmenuOpen ? MENU_ICON_PATHS.downArrow : MENU_ICON_PATHS.rightArrows}
                        alt=""
                        style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                      />
                    </span>
                  )}
                </button>
                {hasSubItems && isSubmenuOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      left: collapsed ? 'calc(100% - 2px)' : 'calc(100% - 8px)',
                      top: '2px',
                      width: '210px',
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '10px',
                      boxShadow: '0 8px 22px rgba(0,0,0,0.14)',
                      zIndex: 40,
                      padding: '8px'
                    }}
                  >
                    {item.subItems?.map((subItem) => (
                      <button
                        key={subItem.key}
                        type="button"
                        onClick={() => {
                          subItem.onClick();
                          setOpenSubmenuKey(null);
                        }}
                        className="traffic-menu-font w-full text-left px-3 py-2 rounded-md text-[15px] transition-colors hover:bg-gray-100"
                        style={{
                          color: subItem.isDanger ? '#b91c1c' : '#1f2937',
                          fontWeight: 500
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

export default SideMenuCard;

